import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { domainAPI } from "../../lib/api";
import type { CustomDomainResponse, DomainWithInstructions } from "../../types/api";
import { Button } from "../common";
import styles from "./CustomDomainSection.module.css";

interface CustomDomainSectionProps {
  organizationId: string;
  podcastId: string;
}

export function CustomDomainSection({ organizationId, podcastId }: CustomDomainSectionProps) {
  const [domains, setDomains] = useState<CustomDomainResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<Record<string, DomainWithInstructions>>({});
  const [verifying, setVerifying] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const loadDomains = useCallback(async () => {
    try {
      const result = await domainAPI.list(organizationId, podcastId);
      setDomains(result);
    } catch {
      toast.error("Failed to load custom domains");
    } finally {
      setLoading(false);
    }
  }, [organizationId, podcastId]);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setAdding(true);
    try {
      const result = await domainAPI.add(organizationId, podcastId, newDomain.trim());
      setDomains((prev) => [...prev, result.domain]);
      setInstructions((prev) => ({ ...prev, [result.domain.id]: result }));
      setExpandedDomain(result.domain.id);
      setNewDomain("");
      setShowAddForm(false);
      toast.success("Domain added! Follow the instructions below to verify ownership.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add domain");
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifying(domainId);
    try {
      const result = await domainAPI.verify(organizationId, podcastId, domainId);
      if (result.verified) {
        toast.success(result.message);
        setDomains((prev) => prev.map((d) => (d.id === domainId ? { ...d, verified: true } : d)));
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setVerifying(null);
    }
  };

  const handleRemove = async (domainId: string) => {
    setRemoving(domainId);
    try {
      await domainAPI.remove(organizationId, podcastId, domainId);
      setDomains((prev) => prev.filter((d) => d.id !== domainId));
      toast.success("Domain removed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove domain");
    } finally {
      setRemoving(null);
    }
  };

  const handleToggleInstructions = async (domainId: string) => {
    if (expandedDomain === domainId) {
      setExpandedDomain(null);
      return;
    }

    setExpandedDomain(domainId);

    if (!instructions[domainId]) {
      try {
        const result = await domainAPI.get(organizationId, podcastId, domainId);
        setInstructions((prev) => ({ ...prev, [domainId]: result }));
      } catch {
        toast.error("Failed to load verification instructions");
      }
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied to clipboard`),
      () => toast.error("Failed to copy"),
    );
  };

  if (loading) {
    return (
      <div className={styles.section}>
        <h2>Custom Domain</h2>
        <p className={styles.loading}>Loading domains...</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div>
          <h2>Custom Domain</h2>
          <p className={styles.sectionDescription}>
            Use your own domain for your podcast's RSS feed and public pages.
          </p>
        </div>
        {!showAddForm && domains.length < 5 && (
          <Button variant="secondary" onClick={() => setShowAddForm(true)}>
            Add Domain
          </Button>
        )}
      </div>

      {showAddForm && (
        <form onSubmit={handleAddDomain} className={styles.addForm}>
          <div className={styles.addFormRow}>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="podcast.example.com"
              className={styles.domainInput}
              disabled={adding}
              /* autoFocus removed for a11y */
            />
            <Button type="submit" variant="primary" loading={adding} loadingText="Adding...">
              Add
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddForm(false);
                setNewDomain("");
              }}
              disabled={adding}
            >
              Cancel
            </Button>
          </div>
          <small className={styles.helpText}>
            Enter the domain you want to use (e.g., podcast.yourdomain.com). You'll need access to
            your domain's DNS settings.
          </small>
        </form>
      )}

      {domains.length === 0 && !showAddForm ? (
        <div className={styles.emptyState}>
          <p>No custom domains configured.</p>
          <p className={styles.helpText}>
            Add a custom domain to give your podcast a professional URL.
          </p>
        </div>
      ) : (
        <div className={styles.domainList}>
          {domains.map((domain) => (
            <div key={domain.id} className={styles.domainCard}>
              <div className={styles.domainHeader}>
                <div className={styles.domainInfo}>
                  <span className={styles.domainName}>{domain.domain}</span>
                  <DomainStatusBadge domain={domain} />
                </div>
                <div className={styles.domainActions}>
                  {!domain.verified && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleToggleInstructions(domain.id)}
                      >
                        {expandedDomain === domain.id ? "Hide Instructions" : "Setup Instructions"}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleVerify(domain.id)}
                        loading={verifying === domain.id}
                        loadingText="Verifying..."
                      >
                        Verify DNS
                      </Button>
                    </>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemove(domain.id)}
                    loading={removing === domain.id}
                    loadingText="Removing..."
                  >
                    Remove
                  </Button>
                </div>
              </div>

              {expandedDomain === domain.id && instructions[domain.id] && (
                <VerificationInstructions
                  instructions={instructions[domain.id].instructions}
                  onCopy={copyToClipboard}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DomainStatusBadge({ domain }: { domain: CustomDomainResponse }) {
  if (domain.sslProvisioned) {
    return <span className={`${styles.badge} ${styles.badgeSuccess}`}>SSL Active</span>;
  }
  if (domain.verified) {
    return <span className={`${styles.badge} ${styles.badgeInfo}`}>Verified</span>;
  }
  return <span className={`${styles.badge} ${styles.badgePending}`}>Pending Verification</span>;
}

interface VerificationInstructionsProps {
  instructions: DomainWithInstructions["instructions"];
  onCopy: (text: string, label: string) => void;
}

function VerificationInstructions({ instructions, onCopy }: VerificationInstructionsProps) {
  return (
    <div className={styles.instructions}>
      <h4>DNS Verification</h4>
      <p className={styles.instructionsDesc}>
        Add one of the following DNS records to verify domain ownership. DNS changes can take up to
        48 hours to propagate.
      </p>

      <div className={styles.methodCard}>
        <h5>Option 1: TXT Record (Recommended)</h5>
        <div className={styles.recordRow}>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Host / Name</span>
            <div className={styles.copyableValue}>
              <code>{instructions.methods.txt.host}</code>
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => onCopy(instructions.methods.txt.host, "Host")}
                title="Copy host"
              >
                📋
              </button>
            </div>
          </div>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Type</span>
            <code>TXT</code>
          </div>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Value</span>
            <div className={styles.copyableValue}>
              <code>{instructions.methods.txt.value}</code>
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => onCopy(instructions.methods.txt.value, "Value")}
                title="Copy value"
              >
                📋
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.methodCard}>
        <h5>Option 2: CNAME Record</h5>
        <div className={styles.recordRow}>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Host / Name</span>
            <div className={styles.copyableValue}>
              <code>{instructions.methods.cname.host}</code>
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => onCopy(instructions.methods.cname.host, "Host")}
                title="Copy host"
              >
                📋
              </button>
            </div>
          </div>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Type</span>
            <code>CNAME</code>
          </div>
          <div className={styles.recordField}>
            <span className={styles.recordLabel}>Target</span>
            <div className={styles.copyableValue}>
              <code>{instructions.methods.cname.target}</code>
              <button
                type="button"
                className={styles.copyButton}
                onClick={() => onCopy(instructions.methods.cname.target, "Target")}
                title="Copy target"
              >
                📋
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
